import { type ManualTask, ManualTaskStatus } from '@compass/client/types/manual_task';
import { EmptyState } from '@components/EmptyState';
import { GenericTable } from '@components/GenericTable';
import { QuickFilters } from '@components/QuickFilters';
import { useStore } from '@store/index';
import { Search, UserCircle, XCircle } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import {
  AssigneeDropdown,
  type AssigneeOption,
  type AssigneeOptionGroup,
} from '@/components/AssigneeDropdown';
import { ErrorContainer } from '@/components/ErrorContainer';
import { FormattedDateRelative } from '@/components/FormattedDateRelative';
import { Breadcrumb, BreadcrumbPortal } from '@/components/Header';
import { Badge } from '@/components/ui/badge';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UserAvatar } from '@/components/UserAvatar';
import type { FiltersConfig } from '@/config/quickFilters.config';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import { useAllOrgTasks } from '@/hooks/useAllOrgTasks';
import { useDebounce } from '@/hooks/useDebounce';
import { useTeamMembersMap } from '@/hooks/useTeams';
import { useUsersByIds } from '@/hooks/useUsers';
import type { TableConfig } from '@/types/table';
import { dateToNaiveString, naiveDateToUtc, naiveStringToDate } from '@/utils/date';
import {
  isPendingTask,
  taskStatusBadgeVariant,
  taskStatusLabel,
  taskTypeLabel,
} from '@/utils/manualTasks';
import { buildRoutePath } from '@/utils/routes';

import { teamOfUser, useAllTasksTeams } from './useAllTasksTeams';

const DEFAULT_PAGE_SIZE = 50;

const TAB_ALL = 'all';
const TAB_OPEN = 'open';
const TAB_COMPLETED = 'completed';

// No badgeColor: QuickFilters' default badge uses theme tokens.
const ALL_TASKS_FILTERS: FiltersConfig = {
  filters: [
    { id: TAB_ALL, name: 'All', filter: {} },
    { id: TAB_OPEN, name: 'Open', filter: {} },
    { id: TAB_COMPLETED, name: 'Completed', filter: {} },
  ],
};

type SearchType = 'task_id' | 'case_record_id';

// Task and case ids are Mongo ObjectIDs; only a complete one is sent, so a half-typed id
// neither 400s nor fires a request per keystroke.
const OBJECT_ID = /^[a-f0-9]{24}$/i;

// Default last 7 days as naive (YYYY-MM-DD) strings.
const getDefaultDateRange = () => {
  const today = new Date();
  const past = new Date();
  past.setDate(today.getDate() - 7);
  return { from: dateToNaiveString(past), to: dateToNaiveString(today) };
};

const AllTasksContainer: React.FC = () => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const timeZone = useStore(state => state.getTimeZone)();

  const [activeTab, setActiveTab] = useState<string>(TAB_ALL);
  const [searchType, setSearchType] = useState<SearchType>('task_id');
  const [searchValue, setSearchValue] = useState('');
  const [assigneeEmails, setAssigneeEmails] = useState<string[]>([]);
  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const [dateFrom, setDateFrom] = useState<string>(defaultRange.from);
  const [dateTo, setDateTo] = useState<string>(defaultRange.to);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const { teams } = useAllTasksTeams();
  const { usersMap: teamUsersMap } = useTeamMembersMap(teams);

  // Team-grouped assignee options. Values are emails (AssigneeDropdown's contract); the
  // request needs user ids, so selected emails are mapped back through emailToId.
  const { assigneeOptions, emailToId } = useMemo(() => {
    const groups: AssigneeOptionGroup[] = [];
    const ids = new Map<string, string>();
    for (const team of teams) {
      const memberIds = [
        ...new Set([...(team.manager_id ? [team.manager_id] : []), ...(team.member_ids ?? [])]),
      ];
      const options: AssigneeOption[] = [];
      for (const id of memberIds) {
        const user = teamUsersMap.get(id);
        if (!user?.email) continue;
        ids.set(user.email, id);
        options.push({
          id,
          name: user.name || undefined,
          email: user.email,
          available: user.available,
        });
      }
      if (options.length) groups.push({ id: team.id ?? '', label: team.name ?? '', options });
    }
    return { assigneeOptions: groups, emailToId: ids };
  }, [teams, teamUsersMap]);

  const debouncedSearch = useDebounce(searchValue.trim(), 300);
  const searchIsId = OBJECT_ID.test(debouncedSearch);
  const searchInvalid = debouncedSearch !== '' && !searchIsId;

  const userIds = useMemo(
    () => assigneeEmails.map(email => emailToId.get(email)).filter((id): id is string => !!id),
    [assigneeEmails, emailToId],
  );

  React.useEffect(() => {
    setCurrentPage(1);
  }, [dateFrom, dateTo, pageSize, activeTab, debouncedSearch, searchType, assigneeEmails]);

  // An id search finds one task or case wherever it is, so it ignores the date range.
  const { isLoading, error, tasks, reachedCap, maxRows } = useAllOrgTasks(
    {
      created_after:
        !searchIsId && dateFrom ? naiveDateToUtc(dateFrom, timeZone, 'start') : undefined,
      created_before: !searchIsId && dateTo ? naiveDateToUtc(dateTo, timeZone, 'end') : undefined,
      user_ids: userIds.length ? userIds : undefined,
      task_id: searchIsId && searchType === 'task_id' ? debouncedSearch : undefined,
      case_record_id: searchIsId && searchType === 'case_record_id' ? debouncedSearch : undefined,
    },
    !searchInvalid,
  );

  // Assignees outside the listed teams still need a name. ponytail: one lookup of up to 100
  // ids (LookupUsers' cap); batch it if the page ever shows more distinct outside assignees.
  const outsideAssigneeIds = useMemo(
    () =>
      [...new Set(tasks.map(t => t.user_id ?? ''))]
        .filter(id => id && !teamUsersMap.has(id))
        .slice(0, 100),
    [tasks, teamUsersMap],
  );
  const { usersMap: outsideUsersMap } = useUsersByIds(outsideAssigneeIds);
  const userById = (id: string | undefined) =>
    id ? (teamUsersMap.get(id) ?? outsideUsersMap.get(id)) : undefined;

  const openTasks = useMemo(() => tasks.filter(t => isPendingTask(t.status)), [tasks]);
  const completedTasks = useMemo(
    () => tasks.filter(t => t.status === ManualTaskStatus.MANUAL_TASK_STATUS_COMPLETED),
    [tasks],
  );

  const tabCounts = useMemo(
    () => ({ All: tasks.length, Open: openTasks.length, Completed: completedTasks.length }),
    [tasks.length, openTasks.length, completedTasks.length],
  );

  const filteredTasks =
    activeTab === TAB_OPEN ? openTasks : activeTab === TAB_COMPLETED ? completedTasks : tasks;

  const displayedTasks = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredTasks.slice(start, start + pageSize);
  }, [filteredTasks, currentPage, pageSize]);

  const hasNextPage = currentPage * pageSize < filteredTasks.length;

  const tableConfig = useMemo(
    (): TableConfig<ManualTask> => ({
      columns: [
        {
          key: 'id',
          header: 'Task ID',
          accessor: item => <span className="font-mono text-xs">{item.id}</span>,
          copyable: true,
          copyValue: item => item.id || '',
        },
        {
          key: 'case_record_id',
          header: 'Case ID',
          accessor: item => <span className="font-mono text-xs">{item.case_record_id}</span>,
          copyable: true,
          copyValue: item => item.case_record_id || '',
        },
        {
          key: 'type',
          header: 'Task type',
          accessor: item => taskTypeLabel(item.type),
        },
        {
          key: 'user_id',
          header: 'Assignee',
          accessor: item => {
            if (!item.user_id) return <span className="text-muted-foreground">Unassigned</span>;
            const user = userById(item.user_id);
            return (
              <span className="flex items-center gap-2">
                <UserAvatar name={user?.name} email={user?.email} size="sm" />
                <span className="truncate">{user?.name || user?.email || item.user_id}</span>
              </span>
            );
          },
        },
        {
          key: 'created_at',
          header: 'Created at',
          accessor: item => <FormattedDateRelative value={item.created_at} />,
        },
        {
          key: 'completed_at',
          header: 'Completed at',
          accessor: item =>
            item.completed_at ? (
              <FormattedDateRelative value={item.completed_at} />
            ) : (
              <span className="text-muted-foreground">N/A</span>
            ),
        },
        {
          key: 'team',
          header: 'Team',
          accessor: item =>
            teamOfUser(teams, item.user_id)?.name ?? (
              <span className="text-muted-foreground">—</span>
            ),
        },
        {
          key: 'status',
          header: 'Status',
          accessor: item => (
            <Badge variant={taskStatusBadgeVariant(item.status)}>
              {taskStatusLabel(item.status)}
            </Badge>
          ),
        },
      ],
      // Tasks span programs, so each row links with its own org/program.
      getRowLink: item => {
        const orgId = item.organization_id || selectedOrganizationId || '';
        if (!orgId || !item.program_id || !item.case_record_id) return '#';
        return buildRoutePath(`/cases/${item.case_record_id}`, orgId, item.program_id);
      },
      getRowKey: item => item.id || '',
      emptyState: {
        title: 'No tasks match your filters',
        description: 'Try adjusting the date range, assignee or search.',
      },
    }),
    // userById reads the two maps below; listing them keeps the cells current.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedOrganizationId, teams, teamUsersMap, outsideUsersMap],
  );

  const breadcrumb = (
    <BreadcrumbPortal>
      <Breadcrumb>
        <Breadcrumb.Item>All Tasks</Breadcrumb.Item>
      </Breadcrumb>
    </BreadcrumbPortal>
  );

  if (!selectedOrganizationId) {
    return (
      <>
        {breadcrumb}
        <EmptyState
          title="No organization selected"
          description="Select an organization to view its tasks."
          icon={XCircle}
          iconClassName="text-muted-foreground"
        />
      </>
    );
  }

  if (error) {
    return (
      <>
        {breadcrumb}
        <ErrorContainer errorMessage={error?.message || 'Failed to load tasks'} />
      </>
    );
  }

  return (
    <>
      {breadcrumb}
      <div className="flex flex-col h-full min-h-0">
        <div className="flex flex-col flex-1 min-h-0 px-6">
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <div className="flex items-center">
              <Select value={searchType} onValueChange={v => setSearchType(v as SearchType)}>
                <SelectTrigger className="w-32 rounded-r-none" aria-label="Search by">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="task_id">Task ID</SelectItem>
                  <SelectItem value="case_record_id">Case ID</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative">
                <Input
                  value={searchValue}
                  onChange={e => setSearchValue(e.target.value)}
                  placeholder="Type to search"
                  aria-label={searchType === 'task_id' ? 'Task ID' : 'Case ID'}
                  aria-invalid={searchInvalid}
                  className="w-72 rounded-l-none border-l-0 pr-8 font-mono"
                />
                <Search className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50" />
              </div>
            </div>
            <AssigneeDropdown
              mode="multi"
              options={assigneeOptions}
              selected={assigneeEmails}
              onChange={setAssigneeEmails}
              placeholder="Select assignee"
              className="min-w-[200px] max-w-[300px] px-3"
              triggerIcon={UserCircle}
              searchPlaceholder="Search assignees..."
            />
            <DateRangePicker
              initialDateFrom={dateFrom ? naiveStringToDate(dateFrom) : undefined}
              initialDateTo={dateTo ? naiveStringToDate(dateTo) : undefined}
              timezone={timeZone}
              onUpdate={({ range }) => {
                if (!range.from) return;
                setDateFrom(dateToNaiveString(range.from));
                setDateTo(dateToNaiveString(range.to ?? range.from));
              }}
              showCompare={false}
            />
          </div>
          {searchInvalid && (
            <p className="mt-2 text-sm text-destructive">
              Enter the full 24-character {searchType === 'task_id' ? 'task' : 'case'} ID.
            </p>
          )}
          {reachedCap && (
            <p className="mt-2 text-sm text-muted-foreground">
              Showing the newest {maxRows} tasks. Narrow the date range or filters to see the rest.
            </p>
          )}

          <div className="mt-4">
            <QuickFilters
              activeFilter={activeTab}
              onFilterChange={setActiveTab}
              filtersConfig={ALL_TASKS_FILTERS}
              counts={tabCounts}
              countsLoading={isLoading}
            />
          </div>

          <GenericTable
            config={tableConfig}
            data={searchInvalid ? [] : displayedTasks}
            loading={isLoading && !searchInvalid}
            error={null}
            currentPage={currentPage}
            hasNextPage={hasNextPage}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
            onPageSizeChange={setPageSize}
            selectionMode={false}
          />
        </div>
      </div>
    </>
  );
};

export default AllTasksContainer;
